<script setup lang="ts">
    import BaseCoverLoader from '@/components/BaseCoverLoader.vue';
    import { Events } from '@/services/Events';
    import { useConfigStore } from '@/store/config.store';
    import { onMounted, computed, reactive, ref, watch } from 'vue';
    import { storeToRefs } from 'pinia';
    import type { IProgressElement } from '@/typescript/interfaces/IProgressElement';

    let progressElements: IProgressElement[] = reactive([]);
    const showOnlyAnimation = ref(false);
    const { isAppLoading } = storeToRefs(useConfigStore());

    const progress = computed(() => {
        if (progressElements.length === 0) {
            return 0;
        }
        let totalValue = 0;
        progressElements.forEach((value) => {
            totalValue += value.progress;
        });

        totalValue /= progressElements.length;
        if (totalValue === 100) {
            progressElements = [];

            return 0;
        }

        if (showOnlyAnimation.value) {
            return 100;
        }
        return totalValue;
    });
    watch(progress, (value) => {
        if (value === 0) {
            isAppLoading.value = false;
        }
    });

    const showLoader = computed(() => progress.value > 0 || showOnlyAnimation.value);

    onMounted(() => {
        isAppLoading.value = true;
        Events.on('loading-start', (modelName: string) => {
            if (!progressElements.find((value) => value.name === modelName)) {
                progressElements.push({
                    name: modelName,
                    progress: 0,
                } as IProgressElement);
            }
        });
        Events.on('loading-progress', (progressIn: IProgressElement) => {
            const elemToChange = progressElements.find((value) => value.name === progressIn.name);
            if (elemToChange) {
                elemToChange.progress = progressIn.progress;
            }
        });
        Events.on('loading-end', (modelName) => {
            const finished = progressElements.find((value) => value.name === modelName);
            if (finished) {
                finished.progress = 100;
            }
        });
        Events.on('loading-animation-only-start', () => {
            showOnlyAnimation.value = true;
        });
        Events.on('loading-animation-only-end', () => {
            showOnlyAnimation.value = false;
        });
    });
</script>

<template>
    <Teleport to="body">
        <BaseCoverLoader
            v-if="showLoader"
            :progress
        />
    </Teleport>
</template>
